import styles from "./checkout-review.module.css";
import { useCheckout } from "BEDROCK/pages/checkout/checkout-provider";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { usePaths } from "BEDROCK/paths";
import { ActivityIndicator } from "BEDROCK/shared-components/activity-indicator";
import { first } from "lodash";
import { ProjectPreview } from "components/common/project-preview";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";

interface CheckoutReviewProps {}

export const CheckoutReview = (props: CheckoutReviewProps) => {
  const { checkout } = useCheckout();
  const { checkoutProjects, isCheckoutLoading } = useProjectStore();
  const { navigateTo, checkout: checkoutPath, processOrder } = usePaths();
  const { fullThumbnailSizeFromProject } = useSizePresets();

  // Get the first (and should be only) project in checkout
  const project = first(checkoutProjects);
  const thumbnailSize = project ? fullThumbnailSizeFromProject(project) : undefined;

  const handleConfirm = () => {
    navigateTo(checkoutPath);
  };

  const handleKeepEditing = () => {
    if (project) {
      navigateTo(processOrder + "/" + project.id);
    }
  };

  if (isCheckoutLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.reviewContainer}>
          <div className={styles.reviewHeader}>
            <h1>REVIEW YOUR ORDER</h1>
          </div>
          <div className={styles.loadingState}>
            <ActivityIndicator size="medium" />
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className={styles.container}>
        <div className={styles.reviewContainer}>
          <div className={styles.reviewHeader}>
            <h1>REVIEW YOUR ORDER</h1>
          </div>
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>
              No project found for review. Please start a new project.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.reviewContainer}>
        <div className={styles.reviewHeader}>
          <h1>REVIEW YOUR ORDER</h1>
        </div>

        <div className={styles.reviewContent}>
          <div className={styles.previewSection}>
            <h2 className={styles.sectionTitle}>Preview</h2>
            <div className={styles.previewImageContainer}>
              {thumbnailSize ? (
                <ProjectPreview
                  project={project}
                  size={thumbnailSize}
                  showOutline={false}
                  showShadow={true}
                />
              ) : (
                <div className={styles.noPreview}>
                  <p>Preview not available</p>
                </div>
              )}
            </div>
            <p className={styles.previewNote}>
              This is a low resolution preview for review purposes only. Your
              final print will be high resolution.
            </p>
          </div>

          <div className={styles.infoSection}>
            <div className={styles.mainInfo}>
              <h2 className={styles.mainInfoTitle}>Quality Review Process</h2>
              <p className={styles.mainInfoText}>
                Your order will be reviewed by our team before printing. If we notice any issues with your design, we'll email you to confirm before proceeding.
              </p>
              <p className={styles.mainInfoText}>
                Please check the preview above for any label misspellings, positioning, or design issues you'd like to correct now.
              </p>
            </div>

            <details className={styles.policyDetails}>
              <summary className={styles.policySummary}>
                Customization Policy & Returns
              </summary>
              <div className={styles.policyContent}>
                <ul className={styles.policyList}>
                  <li>
                    Returns and refunds will not be granted for issues that are visible in this preview (such as label misspellings, incorrect positioning, or design choices).
                  </li>
                  <li>
                    The 14-day cooling-off period does not apply to customizable products like this poster.
                  </li>
                  <li>
                    Refunds are only offered if the delivered product is defective or damaged during shipping.
                  </li>
                </ul>
              </div>
            </details>
          </div>

          <div className={styles.actionButtons}>
            <CTAButton variant="SECONDARY" onClick={handleKeepEditing}>
              Keep Editing
            </CTAButton>
            <CTAButton
              variant="PRIMARY"
              onClick={handleConfirm}
              iconName="CHEVRON-RIGHT"
              iconPosition="RIGHT"
            >
              Confirm & Continue to Checkout
            </CTAButton>
          </div>
        </div>
      </div>
    </div>
  );
};
